// src/pages/payroll/WageRates.jsx
// 시급 설정 — 연도별 시급등급(생활임금/시설관리 등). 등급 추가·전년도 불러오기 지원
import React, { useEffect, useState } from "react";
import api from "../../api";

const num = (v) => { const n = Number(String(v ?? "0").replace(/[, 원]/g, "")); return Number.isFinite(n) ? n : 0; };
const fmt = (n) => Number(n || 0).toLocaleString();

const NOW_Y = new Date().getFullYear();
const YEARS = [];
for (let y = NOW_Y + 5; y >= 2026; y--) YEARS.push(y);   // 2026 ~ (올해+5년)

// 2026이 비어있을 때 한 번에 만들 기본 등급
const DEFAULT_GRADES = ["생활임금", "시설관리 임금"];

export default function WageRates() {
  const [year, setYear] = useState(String(Math.max(NOW_Y, 2026)));
  const [rows, setRows] = useState([]);          // [{id, grade, hourly}]
  const [edit, setEdit] = useState({});          // id -> 시급 입력값(문자열)
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async (y = year) => {
    try {
      const r = await api.get("/payroll/wage-rates/", { params: { year: y } });
      const list = Array.isArray(r.data) ? r.data : [];
      setRows(list);
      const e = {}; list.forEach((x) => { e[x.id] = String(x.hourly); });
      setEdit(e);
    } catch { setRows([]); setEdit({}); }
  };
  useEffect(() => { load(year); /* eslint-disable-next-line */ }, [year]);

  // 등급 추가 (그 연도에 새 grade 생성, 시급 0으로 시작)
  const addGrade = async (grade) => {
    const g = (grade || "").trim();
    if (!g) return;
    if (rows.some((x) => x.grade === g)) { setMsg(`'${g}' 등급이 이미 있어요.`); return; }
    setBusy(true);
    try {
      await api.post("/payroll/wage-rates/", { year: num(year), grade: g, hourly: 0 });
      await load(year);
      setMsg(`'${g}' 등급 추가됨. 시급을 입력하고 저장하세요.`);
    } catch (err) { setMsg("등급 추가 실패: " + (err.response?.data?.detail || err.message)); }
    finally { setBusy(false); }
  };

  const promptAddGrade = async () => {
    const v = window.prompt("추가할 임금항목 이름 (예: 경비 임금, 운전직 임금)");
    if (v) addGrade(v);
  };

  // 기본 등급 일괄 생성 (빈 연도 편의)
  const seedDefaults = async () => {
    setBusy(true);
    try {
      for (const g of DEFAULT_GRADES) {
        if (!rows.some((x) => x.grade === g)) {
          await api.post("/payroll/wage-rates/", { year: num(year), grade: g, hourly: 0 });
        }
      }
      await load(year);
      setMsg("기본 등급(생활임금·시설관리 임금)을 만들었어요. 시급을 입력하고 저장하세요.");
    } catch (err) { setMsg("생성 실패: " + (err.response?.data?.detail || err.message)); }
    finally { setBusy(false); }
  };

  // 전년도 등급+금액 복사 (현재 연도가 비어있을 때 권장)
  const importPrevYear = async () => {
    const prev = num(year) - 1;
    setBusy(true);
    try {
      const r = await api.get("/payroll/wage-rates/", { params: { year: prev } });
      const src = Array.isArray(r.data) ? r.data : [];
      if (src.length === 0) { setMsg(`${prev}년 등급이 없어요.`); setBusy(false); return; }
      for (const s of src) {
        if (!rows.some((x) => x.grade === s.grade)) {
          await api.post("/payroll/wage-rates/", { year: num(year), grade: s.grade, hourly: s.hourly });
        }
      }
      await load(year);
      setMsg(`${prev}년 등급 ${src.length}개를 불러왔어요. 금액 확인 후 저장하세요.`);
    } catch (err) { setMsg("불러오기 실패: " + (err.response?.data?.detail || err.message)); }
    finally { setBusy(false); }
  };

  // 시급 저장 (한 줄)
  const saveRow = async (row) => {
    setBusy(true);
    try {
      await api.post("/payroll/wage-rates/", { year: num(year), grade: row.grade, hourly: num(edit[row.id]) });
      await load(year);
      setMsg(`'${row.grade}' 시급 ${fmt(num(edit[row.id]))}원 저장됨.`);
    } catch (err) { setMsg("저장 실패: " + (err.response?.data?.detail || err.message)); }
    finally { setBusy(false); }
  };

  const removeRow = async (row) => {
    if (!window.confirm(`${year}년 '${row.grade}' 등급을 삭제할까요?`)) return;
    setBusy(true);
    try {
      await api.post("/payroll/wage-rates/delete/", { id: row.id });
      await load(year);
      setMsg(`'${row.grade}' 삭제됨.`);
    } catch (err) { setMsg("삭제 실패: " + (err.response?.data?.detail || err.message)); }
    finally { setBusy(false); }
  };

  const S = {
    wrap: { padding: 16, fontSize: 13, color: "#1f2937" },
    bar: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 12 },
    btn: (c, d) => ({ padding: "6px 12px", background: d ? "#9ca3af" : c, color: "#fff", border: "none", borderRadius: 6, fontWeight: 600, cursor: d ? "not-allowed" : "pointer", fontSize: 12, whiteSpace: "nowrap" }),
    btnSm: (c, d) => ({ padding: "4px 10px", background: d ? "#9ca3af" : c, color: "#fff", border: "none", borderRadius: 6, fontWeight: 600, cursor: d ? "not-allowed" : "pointer", fontSize: 12, whiteSpace: "nowrap" }),
    th: { background: "#f1f5f9", padding: "6px 8px", borderBottom: "1px solid #cbd5e1", textAlign: "left", whiteSpace: "nowrap" },
    thR: { background: "#f1f5f9", padding: "6px 8px", borderBottom: "1px solid #cbd5e1", textAlign: "right", whiteSpace: "nowrap" },
    td: { padding: "6px 8px", borderBottom: "1px solid #eef2f7" },
    tdR: { padding: "6px 8px", borderBottom: "1px solid #eef2f7", textAlign: "right" },
    sel: { padding: "4px 8px", borderRadius: 6, border: "1px solid #cbd5e1" },
    input: { padding: "4px 8px", borderRadius: 6, border: "1px solid #cbd5e1" },
    card: { border: "1px solid #e2e8f0", borderRadius: 8, padding: 14, maxWidth: 620 },
  };

  return (
    <div style={S.wrap}>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>시급 설정</h2>
      <p style={{ color: "#6b7280", marginBottom: 14 }}>
        연도별 시급등급을 관리합니다. 급여명세서에서 근무자마다 어느 등급인지 매핑해서 기본급·시간외 단가에 사용해요.
        등급은 매년 그대로 두고 <b>금액만</b> 바뀌면 [전년도 불러오기]로 복사하세요.
      </p>

      {msg && (
        <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", padding: "6px 10px", borderRadius: 6, marginBottom: 12, color: "#075985" }}>{msg}</div>
      )}

      <div style={S.card}>
        <div style={S.bar}>
          <label style={{ whiteSpace: "nowrap" }}>적용연도</label>
          <select value={year} onChange={(e) => setYear(e.target.value)} style={{ ...S.sel, width: 100 }}>
            {YEARS.map((y) => <option key={y} value={y}>{y}년</option>)}
          </select>
          <button style={S.btnSm("#0891b2", busy)} disabled={busy} onClick={promptAddGrade}>+ 임금항목 추가</button>
          <button style={S.btnSm("#7c3aed", busy)} disabled={busy} onClick={importPrevYear}>↻ 전년도 불러오기</button>
        </div>

        <div style={{ border: "1px solid #e2e8f0", borderRadius: 6, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={S.th}>임금항목(등급)</th>
                <th style={{ ...S.thR, width: 160 }}>시급(원)</th>
                <th style={{ ...S.th, width: 130 }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td style={S.td} colSpan={3}>
                    {year}년 등급이 없습니다.
                    <button style={{ ...S.btnSm("#16a34a", busy), marginLeft: 8 }} disabled={busy} onClick={seedDefaults}>
                      기본 등급 만들기 (생활임금·시설관리)
                    </button>
                  </td>
                </tr>
              ) : rows.map((row) => (
                <tr key={row.id}>
                  <td style={{ ...S.td, fontWeight: 600 }}>{row.grade}</td>
                  <td style={S.tdR}>
  
                    <input
                      type="text"
                      value={edit[row.id] ? fmt(num(edit[row.id])) : ""}
                      onChange={(e) => setEdit((p) => ({ ...p, [row.id]: e.target.value }))}
                      onKeyDown={(e) => e.key === "Enter" && saveRow(row)}
                      placeholder="0"
                      style={{ ...S.input, width: 120, textAlign: "right" }}
                    />


                  </td>
                  <td style={S.td}>
                    <button style={{ ...S.btnSm("#16a34a", busy), marginRight: 4 }} disabled={busy} onClick={() => saveRow(row)}>저장</button>
                    <button style={S.btnSm("#dc2626", busy)} disabled={busy} onClick={() => removeRow(row)}>삭제</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {rows.length > 0 && (
          <p style={{ color: "#94a3b8", fontSize: 12, marginTop: 8 }}>
            현재 {rows.length}개 등급 · {rows.map((r) => `${r.grade} ${fmt(r.hourly)}원`).join(" / ")}
          </p>
        )}
      </div>
    </div>
  );
}
